import { useState } from 'react'
import { Button, Flex, Input, Popover, Space, Tooltip, Typography } from 'antd'
import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { translateError } from '@/shared/api/errors'
import { displayPhone, isAlreadyMasked } from '@/shared/lib/phoneMask'

/** Sabab maydonining chegarasi — serverdagi bilan bir xil */
const REASON_MAX = 200

interface MaskedPhoneProps {
  value: string | null
  /**
   * SERVERDAN ochish. Berilsa — tugma sababni so'raydi va javobni
   * shu funksiyadan oladi; berilmasa — LOKAL ochish (quyidagi izoh).
   *
   * ⚠️ `null` qaytsa «server bu raqamni bermadi» degani — ekran o'sha
   * holatda niqobda qoladi va yolg'on va'da bermaydi.
   */
  onReveal?: (reason: string) => Promise<string | null>
  /**
   * Ochish umuman taklif qilinsinmi.
   *
   * ⚠️ Ro'yxat/tanlov qatorlarida `false`: u yerda tugma qatorning O'ZINI
   * tanlash bilan to'qnashardi (bosish tanlovni ishga tushiradi).
   */
  revealable?: boolean
}

/**
 * Abonent telefoni — SUKUT BO'YICHA NIQOBLANGAN.
 *
 * <h2>⛔ IKKI XIL OCHISH VA ULAR ARALASHTIRILMAYDI</h2>
 *
 * <b>1. SERVERDAN ({@link onReveal} berilgan)</b> — KONTAKT raqami
 * uchun. `POST /api/subscribers/{id}/contact/reveal`: sabab majburiy,
 * amal AUDITGA tushadi, doira serverda tekshiriladi. Bu YAGONA to'liq
 * himoya: qiymat javobda faqat SO'RALGANDA keladi.
 *
 * <b>2. LOKAL ({@link onReveal} berilmagan)</b> — ABONENT YOZUVINING
 * raqami uchun (`subscriber.phone`, kassa katalogidagi `phone`).
 * ⛔ Bu maydonning ochish endpointi UMUMAN YO'Q va u KONTAKT emas:
 * `subscriber.phone` boshqa jadvalda, SMS aynan undan oqadi (ADR-031
 * §2). `/contact/reveal` uni qaytarmaydi.
 *
 * ⛔ VA LOKAL OCHISH — FAQAT EKRAN UCHUN. To'liq qiymat javobda
 * BARIBIR turibdi va uni brauzerning tarmoq panelidan ko'rish mumkin;
 * kim ochgani HECH QAYERDA yozilmaydi. Bu niqob ma'lumot siri emas,
 * EKRAN GIGIENASI: yonidan o'tgan odam, ekran surati, ekranni
 * ulashish. Cheklov egasiga ochiq aytilgan.
 *
 * <h2>⛔ NIQOB MA'LUMOT QATLAMIGA TEGMAYDI</h2>
 * Faqat CHIZISH paytida qo'llanadi. `PUT /api/subscribers/{id}/contact`
 * TO'LIQ ALMASHTIRISH: formaga niqoblangan qiymat tushsa, saqlashda u
 * haqiqiy raqamni BOSIB YOZARDI va `contact_phone_shape` `*` ni
 * taqiqlamaydi — ya'ni server ham rad etmasdi va raqam JIMGINA
 * yo'qolardi. Forma haqiqiy qiymatni AYNAN {@link onReveal} orqali
 * oladi (`SubscriberFormModal`).
 *
 * ⚠️ Server ALLAQACHON niqoblab yuborgan qiymat qayta niqoblanmaydi
 * (`isAlreadyMasked`) — `maskPhone` o'lchov bo'yicha idempotent, lekin
 * unga TAYANILMAYDI: formula bir kun o'zgarsa niqob ikki marta
 * qo'llanardi va natija boshqa bo'lardi.
 */
export function MaskedPhone({ value, onReveal, revealable = true }: MaskedPhoneProps) {
  const { t } = useTranslation()
  const [revealed, setRevealed] = useState<string | null>(null)
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  const shown = revealed ?? displayPhone(value, false)
  if (shown === null) return <>{t('common.dash')}</>

  /*
    ⛔ SERVER NIQOBLAGAN qiymatni LOKAL ochib bo'lmaydi — to'liq raqam
    bu javobda UMUMAN YO'Q. Lokal rejimda tugma chizilsa u hech narsa
    qilmasdi; serverli rejimda esa ochish baribir mumkin.
  */
  const serverMasked = isAlreadyMasked(value)
  const canReveal = revealable && revealed === null && (onReveal !== undefined || !serverMasked)

  const hide = () => {
    setRevealed(null)
    setReason('')
    setError(null)
  }

  const submit = () => {
    if (onReveal === undefined) {
      setRevealed(value)
      setOpen(false)
      return
    }
    setBusy(true)
    setError(null)
    onReveal(reason.trim())
      .then((full) => {
        // ⚠️ `null` — server bu raqamni bermadi. Niqob QOLADI: bo'sh
        // joy yoki xom qiymat ko'rsatish yolg'on bo'lardi.
        if (full !== null) setRevealed(full)
        setOpen(false)
        setReason('')
      })
      .catch((cause: unknown) => setError(cause))
      .finally(() => setBusy(false))
  }

  /*
    ⛔ OCHILGAN qiymat `tel:` havolasi va nusxa olinadigan bo'ladi;
    NIQOBLANGANI esa IKKALASI HAM EMAS: `+9989****567` ga qo'ng'iroq
    qilib bo'lmaydi (havola yolg'on va'da berardi), nusxa olingan
    yulduzchali qator esa hujjatda yoki SMS da mijozning raqami bo'lib
    qolardi.
  */
  if (revealed !== null) {
    return (
      <Flex align="center" gap={6} wrap>
        <Typography.Link href={`tel:${revealed}`} copyable={{ text: revealed }}>
          {revealed}
        </Typography.Link>
        <Tooltip title={t('phone.hide')}>
          <Button
            type="text"
            size="small"
            aria-label={t('phone.hide')}
            icon={<EyeInvisibleOutlined />}
            onClick={hide}
          />
        </Tooltip>
      </Flex>
    )
  }

  const trigger = (
    <Button
      type="text"
      size="small"
      aria-label={t('phone.reveal')}
      icon={<EyeOutlined />}
      onClick={() => (onReveal === undefined ? submit() : setOpen(true))}
    />
  )

  return (
    <Flex align="center" gap={6} wrap>
      <Typography.Text>{shown}</Typography.Text>
      {canReveal ? (
        onReveal === undefined ? (
          // ⚠️ LOKAL rejim: sabab so'ralmaydi, chunki hech qayerga
          // yozilmaydi — soxta sabab maydoni «bu auditga tushadi»
          // degan yolg'on taassurot berardi.
          <Tooltip title={t('phone.localHint')}>{trigger}</Tooltip>
        ) : (
          <Popover
            open={open}
            onOpenChange={(next) => {
              setOpen(next)
              if (!next) setError(null)
            }}
            trigger="click"
            title={t('phone.reveal')}
            content={
              <Space direction="vertical" style={{ width: 280 }} size={8}>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {t('phone.revealWarn')}
                </Typography.Text>
                {/* ⛔ SABAB MAJBURIY: server usiz 400 beradi, lekin
                    so'rov UMUMAN ketmasligi kerak — har urinish
                    auditga tushadi va rad etilgani ham iz qoldirardi. */}
                <Input
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={t('phone.revealReason')}
                  maxLength={REASON_MAX}
                  onPressEnter={() => (reason.trim() === '' ? undefined : submit())}
                />
                <Button
                  type="primary"
                  block
                  loading={busy}
                  disabled={reason.trim() === ''}
                  onClick={submit}
                >
                  {t('phone.reveal')}
                </Button>
                {error ? (
                  <Typography.Text type="danger" style={{ fontSize: 12 }}>
                    {translateError(error, t)}
                  </Typography.Text>
                ) : null}
              </Space>
            }
          >
            <Tooltip title={t('phone.maskedHint')}>{trigger}</Tooltip>
          </Popover>
        )
      ) : (
        /* ⚠️ Tugmasiz holatda ham SABAB aytiladi — aks holda operator
           yulduzchalarni nosozlik deb o'qirdi. */
        <Tooltip title={serverMasked ? t('phone.serverMaskedHint') : t('phone.maskedHint')}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {t('phone.masked')}
          </Typography.Text>
        </Tooltip>
      )}
    </Flex>
  )
}
